import { useWorkExecution } from './WorkExecutionContext';
import React, { useEffect, useRef, useState } from 'react';
import { Modal, Platform, Pressable, ScrollView, StyleSheet, View, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import WorkApprovalPanel from './WorkApprovalPanel';
import WorkApprovalTriggerContent from './WorkApprovalTriggerContent';
import Tooltip from '@/components/Tooltip';
import { useTheme } from '@/context/ThemeContext';
import { useCoworkerStore, useToastStore } from '@/store';
import { workApprovalMode } from '@/constants/workApproval';

export default function WorkApprovalButton({ taskId = null, compact = false, isDisabled = false, openAbove = false }) {
  const execution = useWorkExecution();
  const { colors } = useTheme();
  const { width, height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const safe = Platform.OS === 'ios' ? insets : { left: 0, right: 0, top: 0, bottom: 0 };
  const cardWidth = Math.min(420, width - safe.left - safe.right - 24);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [menuHeight, setMenuHeight] = useState(300);
  const triggerRef = useRef(null);
  const composerMode = useCoworkerStore(s => s.composerActionApproval);
  const taskMode = useCoworkerStore(s => taskId ? s.tasks[taskId]?.actionApproval : null);
  const taskStatus = useCoworkerStore(s => taskId ? s.tasks[taskId]?.status : null);
  const setComposerMode = useCoworkerStore(s => s.setComposerActionApproval);
  const setTaskMode = useCoworkerStore(s => s.setTaskActionApproval);
  const addToast = useToastStore(s => s.actions.addToast);
  const mode = workApprovalMode(execution ? execution.settings.actionApproval : taskId ? taskMode : composerMode);
  const disabled = isDisabled || execution?.disabled || saving || taskStatus === 'queued';
  const narrow = compact || width < 600;

  const choose = async next => {
    if (disabled) return;
    if (next === mode.id) { setOpen(false); return; }
    setSaving(true);
    try {
      if (execution) await execution.update({ actionApproval: next });
      else if (taskId) await setTaskMode(taskId, next);
      else setComposerMode(next);
      setOpen(false);
    } catch (error) { addToast(error?.message || 'Could not change action approvals', 'error'); }
    finally { setSaving(false); }
  };
  const show = () => {
    setOpen(true);
    const node = triggerRef.current;
    if (node?.getBoundingClientRect) { setAnchor(node.getBoundingClientRect()); }
    else if (node?.measureInWindow) node.measureInWindow((x, y, w, h) => { setAnchor({ left: x, top: y, bottom: y + h }); });
  };
  useEffect(() => {
    if (!open || Platform.OS !== 'ios') return undefined;
    const frame = requestAnimationFrame(() => {
      triggerRef.current?.measureInWindow?.((x, y, w, h) => {
        setAnchor({ left: x, top: y, bottom: y + h });
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [open, width, height]);
  const Trigger = Platform.OS === 'macos' ? DropdownMenu.Trigger : Pressable;
  const trigger = (
    <Tooltip label="Choose when Work asks before taking action" disabled={open || disabled}>
      <Trigger {...(Platform.OS === 'macos' ? {} : { onPress: show })} disabled={disabled}
        accessibilityRole="button" accessibilityLabel={`Action approvals: ${mode.label}`}
        accessibilityState={{ expanded: open, disabled, busy: saving }}
        {...(Platform.OS === 'web' ? { 'aria-expanded': open, 'aria-busy': saving } : {})}>
        <WorkApprovalTriggerContent mode={mode} narrow={narrow} disabled={disabled} colors={colors} />
      </Trigger>
    </Tooltip>
  );
  const panel = <WorkApprovalPanel mode={mode} disabled={disabled} onChoose={choose} />;
  if (Platform.OS === 'macos') return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      {trigger}
      <DropdownMenu.Content side={openAbove ? 'top' : 'bottom'} align="start" sideOffset={8}
        style={{ width: Math.min(420, width - 24), maxWidth: width - 24 }}>{panel}</DropdownMenu.Content>
    </DropdownMenu.Root>
  );
  const sheet = width < 600 || !anchor;
  const above = anchor && anchor.top >= menuHeight + 8 && (openAbove || height - anchor.bottom < menuHeight + 8);
  return (
    <View ref={triggerRef} {...(Platform.OS === 'web' ? {} : { collapsable: false })}>
      {trigger}
      <Modal visible={open} transparent animationType={sheet ? 'fade' : 'none'} onRequestClose={() => setOpen(false)}>
        <Pressable style={[styles.backdrop, sheet && styles.sheetBackdrop, sheet && {
          paddingLeft: safe.left, paddingRight: safe.right, paddingBottom: Math.max(24, safe.bottom),
        }]} onPress={() => setOpen(false)}
          {...(Platform.OS === 'web' ? { onKeyDown: event => { if (event.key === 'Escape') { event.stopPropagation(); setOpen(false); } } } : {})}>
          <Pressable testID="work-approval-card" onPress={() => {}} accessibilityViewIsModal onLayout={event => setMenuHeight(event.nativeEvent.layout.height)}
            style={[styles.card, { backgroundColor: colors.solid_color_primary, borderColor: colors.border_color_secondary,
              width: cardWidth, maxHeight: height - safe.top - safe.bottom - 32 }, !sheet && { position: 'absolute',
              left: Math.max(safe.left + 12, Math.min(anchor.left, width - safe.right - cardWidth - 12)),
              top: Math.max(safe.top + 8, Math.min(above ? anchor.top - menuHeight - 6 : anchor.bottom + 6, height - safe.bottom - menuHeight - 8)) }]}>
            <ScrollView keyboardShouldPersistTaps="handled">{panel}</ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  backdrop: { flex: 1 },
  sheetBackdrop: { backgroundColor: '#0006', alignItems: 'center', justifyContent: 'flex-end', paddingBottom: 24 },
  card: { borderWidth: 1, borderRadius: 18, overflow: 'hidden', ...Platform.select({ web: { boxShadow: '0 8px 32px #0003' } }) },
});
